
import React from 'react';
import { Edit2, Trash2, MapPin, Clock, User } from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter,
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ItineraryEvent } from '@/types/itinerary';
import { formatTimeTo12Hour } from '@/utils/dateUtils';

interface EventCardProps {
  event: ItineraryEvent;
  onEdit?: (event: ItineraryEvent) => void;
  onDelete?: (eventId: string) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onEdit, onDelete }) => {
  // Format time display with start and end time if available
  const formatTimeDisplay = () => {
    if (!event.time) return null;
    
    let timeDisplay = formatTimeTo12Hour(event.time);
    
    if (event.end_time) {
      timeDisplay += ` - ${formatTimeTo12Hour(event.end_time)}`;
    }
    
    return timeDisplay;
  };

  return (
    <Card className="mb-4 overflow-hidden border-0 shadow-md relative animate-fade-in">
      {/* Enhanced teal gradient styling */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0EA5E9]/10 to-[#22D3EE]/5 pointer-events-none" />
      <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#0EA5E9] to-[#22D3EE]" />
      
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0A3D62] to-[#1EAEDB]">
            {event.title}
          </CardTitle>
          
          {formatTimeDisplay() && (
            <div className="flex items-center text-sm text-muted-foreground bg-muted/60 backdrop-blur-sm px-2 py-1 rounded-md">
              <Clock size={14} className="mr-1 text-[#0EA5E9]" />
              <span>{formatTimeDisplay()}</span>
            </div>
          )}
        </div>
        
        {/* Add user name with User icon if available */}
        {event.user_name && (
          <div className="flex items-center mt-1 text-sm font-medium text-[#0A3D62]">
            <User size={14} className="mr-1 text-[#0EA5E9]" />
            {event.user_name}
          </div>
        )}
        
        {event.location && (
          <CardDescription className="flex items-center text-muted-foreground mt-1">
            <MapPin size={14} className="mr-1 text-[#0EA5E9]" />
            {event.location}
          </CardDescription>
        )}
      </CardHeader>
      
      {event.description && (
        <CardContent className="pt-0 pb-2">
          <p>{event.description}</p>
        </CardContent>
      )}
      
      {(onEdit || onDelete) && (
        <CardFooter className="pt-0 pb-3 flex justify-end space-x-2">
          {onEdit && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onEdit(event)}
              className="h-8 text-xs hover:bg-[#0EA5E9]/10 hover:text-[#0A3D62]"
            >
              <Edit2 size={14} className="mr-1" /> Edit
            </Button>
          )}
          
          {onDelete && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this event?')) {
                  onDelete(event.id);
                }
              }}
              className="h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
};

export default EventCard;
